import { createVeriffFrame } from "@veriff/incontext-sdk";
import { useEffect, useState, useCallback, useRef, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as bootstrap from "bootstrap";
import { serverPost, serverGet } from "../../../../middleware/http";
import { apiConst, appConst } from "../../../../services/constant/constant";
import { ecnSession, getMemberId, getMsg, getSearchParams, updateSearchParams } from "../../../../services/utils/validation";
import { useLanguageRes } from "../../../../services/Context/LanguageContext";
import ProgressContext from "../../../../services/utils/progress";
import NotifyContext from "../../../../services/utils/notify";
import { useProfile } from "../../../../services/utils/profileContext";
import { BouncingDots } from "../../../../services/utils/BouncingDots";

const STATUS = {
    ...appConst.veriffStatus,
};

export default function VerifyIdentity(props) {

    const { getLanguageContent } = useLanguageRes();
    const navigate = useNavigate();
    const { progParams } = useContext(ProgressContext);
    const { notifyToast } = useContext(NotifyContext);

    const [isVeriffLoaded, setIsVeriffLoaded] = useState(false);
    const [errorText, setErrorText] = useState(null);
    const [isIdentityVerified, setIsIdentityVerified] = useState(false);
    const [session, setSession] = useState({});
    const [retryCount, setRetryCount] = useState(0); // Add retry counter to force re-rendering
    const [statusInfo, setStatusInfo] = useState({
        statusId: STATUS.inProgress,
        message: getLanguageContent("iduvim"),
        isLoading: true
    });

    const modalConfirmRef = useRef(null);
    const veriffFrameRef = useRef(null);
    const intervalRef = useRef(null);

    // Reset function to properly reset all verification states
    const resetVerificationState = () => {
        if (veriffFrameRef.current) {
            try {
                veriffFrameRef.current.close();
            } catch (error) {
                console.error("Error closing Veriff frame:", error);
            }
            veriffFrameRef.current = null;
        }
        setIsVeriffLoaded(false);
        setSession(null);
        setIsIdentityVerified(false);
        setErrorText(null);
        setRetryCount(prev => prev + 1);
        document.body.style.overflow = 'auto';
    };

    const getSession = async (isretry) => {
        try {
            setErrorText(null);
            progParams(true);

            const memberId = await getMemberId();

            const sessionId = getSearchParams("sessionId") || session?.sessionId;

            //Need to get the existing session or create a new one  decision/{memberId}
            const res = await serverPost(`${apiConst.getActiveSession}${memberId}${sessionId ? `/${sessionId}` : ""}`, {});

            // Already-approved sessions return the plain string "VERIFIED" (no sessionUrl),
            // so this must be checked before the sessionUrl guard.
            if (res?.data == "VERIFIED") {
                setIsIdentityVerified(true);
                ecnSession(true, "extIdVerified");
            } else if (!res?.data?.sessionUrl || !res?.data?.sessionId) {
                setErrorText(getMsg("200")); // 200
                return;
            } else {
                setSession(res?.data);
                if (props?.externalProp?.workFlowSteps)
                    updateSearchParams("sessionId", res.data?.sessionId);
            }

            if (!isretry && props?.externalProp?.workFlowSteps) {
                setTimeout(() => {
                    openModal();
                }, 2000);
            }
            return res.data?.sessionUrl;

        } catch (error) {
            setErrorText(getMsg("200")); // 200
        } finally {
            progParams(false);
        }
    }

    const startVerifSession = async (isRetry) => {
        try {
            if (!props?.isAutoStart && isIdentityVerified) {
                props?.externalProp?.handleServiceFlow("REC", props?.externalProp?.workFlowSteps, "", true)
            } else {
                // Reset state if this is a retry
                if (isRetry) {
                    resetVerificationState();
                }

                let url = session?.sessionUrl;
                if (!url)
                    url = await getSession(true);

                if (url) {
                    const frame = createVeriffFrame({
                        url: url,
                        onEvent: (msg) => {
                            document.body.style.overflow = 'auto';
                            switch (msg) {
                                case "FINISHED":

                                    if (!props?.isAutoStart)
                                        pollStatus();
                                    setIsVeriffLoaded(true);
                                    break;

                                case "CANCELED":
                                    const _canceledText = getLanguageContent("idueiv");
                                    resetVerificationState();
                                    setErrorText(_canceledText);
                                    if (props?.isAutoStart)
                                        notifyToast(undefined, undefined, undefined, undefined, _canceledText);
                                    break;

                                default:
                                    console.log("Unhandled event:", msg);
                                    break;
                            }
                        }
                    });
                    veriffFrameRef.current = frame;
                }
            }

        } catch (error) {
            console.error("Error in startVerifSession:", error);
        }
    }

    const openModal = () => {
        const modal = new bootstrap.Modal(document.getElementById("modalConfirm"));
        modal.show();
    }

    const navigateBack = () => {
        if (isIdentityVerified) {
            props?.externalProp?.previousServices();
        } else {
            navigate("/dashboard");
        }
    }

    const checkVerificationStatus = useCallback(async () => {
        try {
            const memberId = await getMemberId();
            const sessionId = getSearchParams("sessionId") || session?.sessionId;
            const res = await serverGet(`${apiConst.externalIdentityStatus}${sessionId}/${memberId}`);

            if (!res?.data) {
                return null;
            }

            return res.data;
        } catch (error) {
            console.error("Some error as occured", error);
            return null;
        }
    }, []);

    const handleStatusUpdate = useCallback((data) => {
        if (!data) return false;

        const { statusId } = data;

        if (statusId === STATUS.approved) {
            setIsIdentityVerified(true);
            ecnSession(true, "extIdVerified");
            setStatusInfo({ statusId, message: getLanguageContent("iduvis"), isLoading: false });
            setTimeout(() => props?.externalProp?.handleServiceFlow("REC", props?.externalProp?.workFlowSteps, "", true), 4000);
            return true;
        }

        if (statusId === STATUS.resubmission || statusId === STATUS.declined) {
            setSession(null);
            setIsIdentityVerified(false);
            setStatusInfo({ statusId, message: getLanguageContent("iduwcv"), isLoading: false, isRetry: true });
            return true;
        }

        if (statusId === STATUS.inProgress || statusId === STATUS.created) {
            setStatusInfo({
                statusId: STATUS.inProgress,
                message: getLanguageContent("iduvim"),
                isLoading: true
            });
            return false;
        }

        return false;
    }, [statusInfo]);

    const pollStatus = () => {
        let attempts = 0;
        const maxAttempts = 30; // Stop polling after ~2.5 minutes (30 * 5 seconds)
        let currentIntervalId = null;

        const checkStatus = async () => {
            try {
                attempts++;
                const data = await checkVerificationStatus();
                const shouldStop = handleStatusUpdate(data);

                if (shouldStop || attempts >= maxAttempts) {
                    if (currentIntervalId) {
                        clearInterval(currentIntervalId);
                        currentIntervalId = null;
                    }
                    intervalRef.current = null;

                    if (attempts >= maxAttempts && !shouldStop) {
                        setStatusInfo({
                            statusId: STATUS.inProgress,
                            message: getLanguageContent("iduvtl"),
                            isLoading: false,
                            isRetry: true,
                            isTimeout: true
                        });
                    }
                }
            } catch (err) {
                console.error("Error while polling status:", err);
            }
        };

        // Clear any existing interval before starting new one
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        //Initial check
        checkStatus();

        // Poll every 5 seconds
        currentIntervalId = setInterval(checkStatus, 5000);
        intervalRef.current = currentIntervalId;
    }

    const showManualUpload = (data) => {
        if (data?.statusId === STATUS.resubmission || data?.statusId === STATUS.declined) {
            return true;
        } else if (data?.statusId === STATUS.inProgress && data?.isTimeout) {
            return true;
        } else if (data?.statusId === STATUS.inProgress && data?.isLoading) {
            return true;
        }

        return false;
    }

    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (props?.externalProp?.workFlowSteps)
            getSession();

        return () => {
            document.body.style.overflow = 'auto';
            if (veriffFrameRef.current) {
                try {
                    veriffFrameRef.current.close();
                } catch (error) {
                    console.error("Error closing Veriff frame on unmount:", error);
                }
                veriffFrameRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (props?.isAutoStart && !props?.isShowLink && !props?.externalProp?.workFlowSteps) {
            setTimeout(() => {
                startVerifSession(false);
            }, 2000);
        };

    }, [])

    if (props?.externalProp?.workFlowSteps && errorText) {
        return (
            <SessionStatus
                statusInfo={{ message: errorText, isRetry: true }}
                toggleExternalIdentity={props?.toggleExternalIdentity}
                startVerification={startVerifSession}
                showManual={showManualUpload(statusInfo)}
            />
        );
    }

    return (
        <>
            {(props?.isAutoStart && props?.isShowLink) && (
                <>
                    <small className="text-success text-decoration-underline pointer-class" onClick={() => startVerifSession()}>{getLanguageContent("iduciv")}</small>
                </>
            )}

            {props?.externalProp?.workFlowSteps && isVeriffLoaded && (
                <SessionStatus
                    statusInfo={statusInfo}
                    pollStatus={pollStatus}
                    toggleExternalIdentity={props?.toggleExternalIdentity}
                    startVerification={startVerifSession}
                    showManual={showManualUpload(statusInfo)}
                />
            )}

            <div
                ref={modalConfirmRef}
                className="modal fade"
                id="modalConfirm"
                data-bs-backdrop="static"
                data-bs-keyboard="false"
                tabIndex="-1"
                aria-labelledby="staticBackdropLabelConfirm"
                aria-hidden="true"
            >
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            {!isIdentityVerified && <p className="modal-title" id="staticBackdropLabelConfirm">{getLanguageContent('iduidr')}</p>}
                        </div>
                        <div className="modal-body text-center">
                            {isIdentityVerified ? getLanguageContent('iduvis') : getLanguageContent('idutci')}
                        </div>
                        <div className="modal-footer d-flex justify-content-end">
                            <button
                                data-bs-dismiss="modal"
                                type="button"
                                onClick={() => startVerifSession()}
                                className="btn btn-primary me-2"
                            >
                                {getLanguageContent("continue")}
                            </button>

                            {isIdentityVerified && (
                                <button
                                    data-bs-dismiss="modal"
                                    type="button"
                                    onClick={navigateBack}
                                    className="btn btn-secondary"
                                >
                                    {isIdentityVerified ? getLanguageContent("bck") : getLanguageContent("iduvcv")}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

const SessionStatus = ({ statusInfo, startVerification, pollStatus, toggleExternalIdentity, showManual }) => {
    const { getLanguageContent } = useLanguageRes();
    const { getPatient } = useProfile();

    const handleRetry = () => {
        if (statusInfo?.isTimeout) {
            // For timeout: Resume polling
            if (pollStatus) {
                pollStatus();
            }
        } else {
            // For all other retries: Start new verification
            if (startVerification) {
                startVerification(true);
            }
        }
    };

    return (
        <div
            className="bg-white rounded d-flex justify-content-center align-items-center"
            style={{ minHeight: "78.5vh" }}
        >
            <div className="text-center my-auto">
                <div className="w-100">
                    {statusInfo?.isLoading && (
                        <div className="mb-3"><BouncingDots text="" /></div>
                    )}
                    <h3 className="h2  mx-4">
                        {statusInfo?.message}
                    </h3>
                </div>
                {statusInfo?.isRetry && (
                    <div className="my-2">
                        <button type="button" className="btn btn-primary m-2" onClick={handleRetry}>
                            {getLanguageContent("iduvtr")} {/* Try Again */}
                        </button>
                        {showManual && toggleExternalIdentity ? (
                            <button type="button" className="btn btn-primary m-2"
                                onClick={() => {
                                    toggleExternalIdentity()
                                    getPatient();
                                }}>
                                {getLanguageContent("idumtp")}
                            </button>
                        ) : (
                            <>
                                <button type="button" className="btn btn-primary m-2" to={`/dashboard`}>
                                    {getLanguageContent("iduvbt")}
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}